import { useAuth } from '@/contexts/AuthContext';
import { ConversationView, SourceSetSelection } from './ConversationView';

interface EnquiryConversationProps {
  entityId: string;
  /** Selection + toggles for the extraction source set (analyst only). */
  selection?: SourceSetSelection;
  onToggleMessage?: (messageId: string) => void;
  onToggleAttachment?: (attachmentId: string) => void;
  /** Lifts the resolved client-thread id so the page binds the files panel
   *  to the same thread the analyst is reading. */
  onThreadResolved?: (threadId: string | undefined) => void;
}

/**
 * Enquiry conversation for the main column. Mirrors EntityChatAside's
 * role split — admins get the single MERGED conversation (internal
 * notes interleaved, composer "Internal" checkbox), clients get the
 * client panel — rendering the wider {@link ConversationView} and
 * wiring the extraction source-set selection.
 */
export function EnquiryConversation({
  entityId,
  selection,
  onToggleMessage,
  onToggleAttachment,
  onThreadResolved,
}: Readonly<EnquiryConversationProps>) {
  const auth = useAuth();
  const user = auth?.user;
  const isAdmin = user?.isAdministrator === true;
  const isClient = user?.isClient === true;

  if (!user || (!isAdmin && !isClient)) {
    return null;
  }

  if (!isAdmin) {
    return (
      <ConversationView
        entityType="enquiry"
        entityId={entityId}
        kind="client_thread"
        canSendPrivate={false}
        onThreadResolved={onThreadResolved}
      />
    );
  }

  return (
    <ConversationView
      entityType="enquiry"
      entityId={entityId}
      kind="client_thread"
      canSendPrivate
      mergeInternal
      selectable
      selection={selection}
      onToggleMessage={onToggleMessage}
      onToggleAttachment={onToggleAttachment}
      onThreadResolved={onThreadResolved}
    />
  );
}
